import React, { FC, useEffect, useState } from "react"
import { Script, graphql } from "gatsby"
import styled from "styled-components"
import xss from "xss"

import { AshbyJobPostPageQueryQuery } from "@graphqlTypes"

import Button, { BUTTON_SIZES } from "@components/Button/Button"
import Hero from "@components/Hero/Hero"
import SEO from "@components/SEO/SEO"
import Tabs, { TabsDIV } from "@components/Tabs/Tabs"
import { ContentDIV } from "@components/Content/Content"
import { isClientSide, smoothScrollToElement } from "@utils"
import TextLink from "@components/TextLink/TextLink"

declare global {
  interface Window {
    __Ashby: any
  }
}

const AshbyJobPostPageHERO = styled(Hero)`
  background: var(--color--slate-100);

  > div:first-child {
    padding: var(--spacing--M) 0 var(--spacing--L);
    text-align: center;
  }

  h1 {
    margin: 0 auto var(--spacing--S);
    max-width: 24ch;
  }
`

const AshbyJobPostPageGridDIV = styled.div.attrs({ className: "grid" })`
  align-items: flex-start;
  padding: 0 0 var(--spacing--L);

  &:first-of-type {
    padding: var(--spacing--S) 0 0;
  }

  .Content {
    h1:first-child,
    h2:first-child { 
      margin-top: 0;
    }

    h1,
    h2,
    h3 {
      strong {
        color: var(--color--text-headline);
      }
    }
  }

  .Tabs--Items {
    justify-content: flex-start;
  }
`

const AshbyJobPostPageDetailsDIV = styled.div`
  dl {
    max-width: 100%;

    dt {
      font-weight: 500;
      color: var(--color--text-headline);
      margin: 0 0 var(--spacing--base-0-5);
    }

    dd {
      font-size: var(--font-size--XS);
      margin: 0 0 var(--spacing--XS);
      padding: 0 0 var(--spacing--XS);
      border-bottom: 1px solid var(--color--charcoal-200);

      &:last-of-type {
        border-bottom: none;
      }
    }
  }
`

enum TABS {
  DESCRIPTION = "DESCRIPTION",
  APPLICATION = "APPLICATION"
}

export const Head: FC<{ data: AshbyJobPostPageQueryQuery }> = ({ data: { ashbyJobPosting } }) => ashbyJobPosting ? (
  <SEO
    description="Help Scout is a remote-first company, letting employees do their best work from anywhere in the world. Explore current openings and competitive offerings."
    permalink={`/company/careers/${ashbyJobPosting.id}/`}
    title={`${ashbyJobPosting.title} – Careers at Help Scout`}
    image="/images/pages/company/careers/CareersPage--meta-evolution.png"
    robots={ashbyJobPosting.isListed ? undefined : "noindex,nofollow"}
  />
) : null

const AshbyJobPostPage: FC<{ data: AshbyJobPostPageQueryQuery }> = ({ data: { ashbyJobPosting } }) => {
  if (!ashbyJobPosting) return null

  const {
    departmentName,
    employmentType,
    id,
    info,
    locationName,
    title,
  } = ashbyJobPosting

  const [activeTab, setActiveTab] = useState<TABS>(TABS.DESCRIPTION)
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false)

  useEffect(() => {
    if (!isClientSide() || isIframeLoaded) return

    const initApplication = () => {
      if (!window.__Ashby.isInitialized) {
        setTimeout(initApplication, 100)
        return
      }

      setIsIframeLoaded(true)

      window.__Ashby.iFrame().load({ jobPostingId: id })
    }

    initApplication()
  }, [isIframeLoaded])

  return (
    <>
      <Script
        dangerouslySetInnerHTML={{
          __html: `
window.__Ashby = {
  settings: {
    ashbyBaseJobBoardUrl: "https://jobs.ashbyhq.com/helpscout",
    displayMode: "application-form-only",
    autoLoad: false,
    customCssUrl: "https://www.helpscout.com/css/ashbyApplicationForm.css",
  }
}
            `
        }}
      />
      <Script
        id="ashby-script"
        src="https://jobs.ashbyhq.com/helpscout/embed?version=2"
      />
      <AshbyJobPostPageHERO>
        <div>
          <h1 className="font--display-M">{title}</h1>
          <TextLink
            className="font--text-S"
            href="/company/careers/#open-roles"
          >
            View Open Roles
          </TextLink>
        </div>
      </AshbyJobPostPageHERO>
      <div className="container" id="ashby-tabs">
        <AshbyJobPostPageGridDIV>
          <div className="column column--2-12-L">
            <Tabs
              activeOption={activeTab}
              onClickOption={(value) => {
                setActiveTab(value as TABS)
                smoothScrollToElement("ashby-tabs")
              }}
              options={[
                {
                  title: "Overview",
                  value: TABS.DESCRIPTION,
                },
                {
                  title: "Application",
                  value: TABS.APPLICATION,
                }
              ]}
            />
          </div>
        </AshbyJobPostPageGridDIV>
        <AshbyJobPostPageGridDIV>
          <div className="column column--1-5-M column--2-5-L">
            <AshbyJobPostPageDetailsDIV>
              <dl>
                <dt className="type--headline-medium-S">Location</dt>
                <dd>{locationName}</dd>
                <dt className="type--headline-medium-S">Type</dt>
                <dd>{employmentType}</dd>
                <dt className="type--headline-medium-S">Department</dt>
                <dd>{departmentName}</dd>
                {info?.compensation?.compensationTierSummary ? (
                  <>
                    <dt className="type--headline-medium-S">Compensation</dt>
                    <dd>{info.compensation.compensationTierSummary}</dd>
                  </>
                ) : null}
              </dl>
            </AshbyJobPostPageDetailsDIV>
          </div>
          <div className="column column--5-13-M column--5-12-L" style={{ display: activeTab === TABS.DESCRIPTION ? "block" : "none" }}>
            {info?.descriptionHtml && (
              <>
                <ContentDIV dangerouslySetInnerHTML={{ __html: xss(info.descriptionHtml) }} id="Content" />
                <Button
                  onClick={() => {
                    setActiveTab(TABS.APPLICATION)
                    smoothScrollToElement("ashby-tabs")
                  }}
                  size={BUTTON_SIZES.L}
                >
                  Apply now
                </Button>
              </>
            )}
          </div>
          <div className="column column--5-13-M column--5-12-L" style={{ display: activeTab === TABS.APPLICATION ? "block" : "none" }}>
            <div
              id="ashby_embed"
              className="application-form-embed-container"
            />
          </div>
        </AshbyJobPostPageGridDIV>
      </div>
    </>
  )
}

export const pageQuery = graphql`
  query AshbyJobPostPageQuery($id: String!) {
    ashbyJobPosting(id: { eq: $id }) {
      departmentName
      employmentType
      id
      info {
        descriptionHtml
        compensation {
          compensationTierSummary
        }
      }
      isListed
      locationName
      title
    }
  }
`

export default AshbyJobPostPage
